import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Paper,
  ListItemText,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Fade,
  CircularProgress,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Edit,
  Visibility,
  Save,
} from '@mui/icons-material';
import axios from 'axios';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import ProfilePhoto from '../Components/EditProfileSection/ProfilePhoto.jsx';
import CoverPhoto from '../Components/EditProfileSection/CoverPhoto.jsx';
import { useAuth } from '../../../utils/AuthContext';

const API_URL = 'https://tdtlworld.com/dss-kaas-backend/lms/user/';
const GOLD = '#D4AF37';

const AdminEditProfile = () => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    nickname: '',
    visibility: 'Public',
  });

  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('edit');
  const token = localStorage.getItem('jwttoken');

  const { userData, refreshUserData } = useAuth();

  useEffect(() => {
    if (userData) {
      setFormData({
        firstName: userData.first_name || '',
        lastName: userData.last_name || '',
        nickname: userData.nickname || '',
        visibility: userData.visibility || 'Public',
      });
      setLoading(false);
    }
  }, [userData]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleVisibilityChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await axios.patch(
        API_URL,
        {
          first_name: formData.firstName,
          last_name: formData.lastName,
          nickname: formData.nickname,
          visibility: formData.visibility,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      refreshUserData(); // Refresh global state
      Swal.fire({ icon: 'success', title: 'Success!', text: 'Admin profile updated successfully!', timer: 1500, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error!', text: 'Failed to update profile.' });
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress sx={{ color: GOLD }} />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight={800} color={theme.palette.mode === 'dark' ? '#FFFFFF' : 'text.primary'}>
            Manage Profile
          </Typography>
          <Typography variant="body2" color="text.secondary">Customize your administrative identity.</Typography>
        </Box>
        <Button 
          variant="outlined" 
          startIcon={<Visibility />}
          component={Link}
          to="/db-shiksha/kaas/admin/dashboard"
          sx={{ borderRadius: '50px', fontWeight: 700, color: GOLD, borderColor: GOLD }}
        >
          View Dashboard
        </Button>
      </Box>

      <Grid container spacing={4}>
        <Grid item xs={12} md={3}>
          <Paper sx={{ borderRadius: '20px', overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button 
                 fullWidth 
                 variant={activeSection === 'edit' ? "contained" : "text"} 
                 onClick={() => setActiveSection('edit')} 
                 sx={{ justifyContent: 'flex-start', borderRadius: '12px', bgcolor: activeSection === 'edit' ? GOLD : 'transparent' }}
                 startIcon={<Edit />}
              >
                 Edit Details
              </Button>
              <Button 
                 fullWidth 
                 variant={activeSection === 'profile-photo' ? "contained" : "text"} 
                 onClick={() => setActiveSection('profile-photo')} 
                 sx={{ justifyContent: 'flex-start', borderRadius: '12px', bgcolor: activeSection === 'profile-photo' ? GOLD : 'transparent' }}
              >
                 Profile Photo
              </Button>
              <Button 
                 fullWidth 
                 variant={activeSection === 'cover-photo' ? "contained" : "text"} 
                 onClick={() => setActiveSection('cover-photo')} 
                 sx={{ justifyContent: 'flex-start', borderRadius: '12px', bgcolor: activeSection === 'cover-photo' ? GOLD : 'transparent' }}
              >
                 Cover Photo
              </Button>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={9}>
          <Fade in={true} timeout={500}>
            <Paper sx={{ p: { xs: 3, md: 5 }, borderRadius: '24px', border: '1px solid', borderColor: 'divider' }}>
              {activeSection === 'edit' && (
                <Box component="form" onSubmit={handleSubmit}>
                  <Typography variant="h6" fontWeight={800} sx={{ mb: 4 }}>Admin Identity Info</Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="First Name" name="firstName" value={formData.firstName} onChange={handleChange} required />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Last Name" name="lastName" value={formData.lastName} onChange={handleChange} required />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Nickname" name="nickname" value={formData.nickname} onChange={handleChange} required />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Visibility</InputLabel>
                        <Select value={formData.visibility} label="Visibility" onChange={(e) => handleVisibilityChange('visibility', e.target.value)}>
                          <MenuItem value="Public">Public</MenuItem>
                          <MenuItem value="Only Me">Only Me</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sx={{ mt: 2 }}>
                      <Button variant="contained" size="large" type="submit" startIcon={<Save />} disabled={isSaving} sx={{ px: 5, borderRadius: '50px', fontWeight: 800, bgcolor: GOLD }}>
                        {isSaving ? 'Saving...' : 'Save Admin Profile'}
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              )}
              {activeSection === 'profile-photo' && <ProfilePhoto />}
              {activeSection === 'cover-photo' && <CoverPhoto />}
            </Paper>
          </Fade>
        </Grid>
      </Grid>
    </Container>
  );
};

export default AdminEditProfile;
